import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Layout, PageHeader, Tabs, Button, Badge } from 'antd'
import HeaderNavigation from '../../components/HeaderNavigation'
import ApplicationsTable from '../../components/application/ApplicationsTable'
import ApplicationItemsTable from '../../components/application/ApplicationItemsTable'

import ChooseProjectModal from '../../components/project/ChooseProjectModal'
import { setProject } from '../../store/projects'
import { setApplication, setApplicationItem } from '../../store/application'
import { userSelector } from '../../store/session'
import './style.css'
import { getApplicationCount } from '../../services/application'
import {
  APP_STATUS_ACTIVE,
  APP_STATUS_AGREE,
  APP_STATUS_DELIVERY,
  APP_STATUS_DONE,
  APP_STATUS_DRAFT,
  APP_STATUS_PAY,
  APP_STATUS_PROGRESS,
} from '../../components/reports/constants'

const { Content } = Layout
const { TabPane } = Tabs

const TabBadge = ({ title, count, isActive }) => {
  const itemPadding = count < 10 ? 20 : 35
  const leftOffset = count < 10 ? 0 : -5
  return (
    <Badge
      count={count || 0}
      color={isActive ? 'volcano' : 'grey'}
      offset={[leftOffset, 10]}
      style={{ marginRight: 5 }}
    >
      <span style={{ paddingRight: count > 0 ? itemPadding : 0 }}>{title}</span>
    </Badge>
  )
}

const Applications = () => {
  const navigate = useNavigate()
  const { user } = useSelector(userSelector)
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useDispatch()
  const [applicationState, setApplicationState] = useState(APP_STATUS_ACTIVE)
  const [showProjectDialog, setShowProjectDialog] = useState(false)
  const [counts, setCounts] = useState({ applicationItems: { count: 0 }, applications: {} })

  const isForeman = useMemo(() => user?.role?.type === 'foreman', [user])
  const isProject = useMemo(() => user?.role?.type === 'project_manager', [user])

  useEffect(() => {
    if (user?.role?.type === 'project_manager' && applicationState === APP_STATUS_DRAFT) {
      setSearchParams({ state: APP_STATUS_AGREE })
    }
    getCounts()
  }, [applicationState, setSearchParams, user])

  const getCounts = () => {
    const ids = user?.projects?.map((item) => item.id)
    if (ids && ids.length > 0) {
      getApplicationCount(ids).then((response) => {
        if (response?.data) {
          const result = {
            applicationItems: response?.data?.applicationItems[0],
            applications: response?.data?.applications.reduce((acc, item) => {
              acc[item.current_state] = item.count
              return acc
            }, {}),
          }
          setCounts(result)
        }
      })
    }
  }

  useEffect(() => {
    const state = searchParams.get('state')
    if (state) {
      setApplicationState(state)
    }
  }, [searchParams])

  useEffect(() => {
    dispatch(
      setProject({
        project: null,
      })
    )
    dispatch(setApplication({ application: null }))
  }, [])

  const onTabSelect = useCallback((key) => {
    setSearchParams({ state: key })
  }, [])

  const onStateChange = (newState) => {
    setSearchParams({ state: newState })
  }

  const onProjectDialogCancel = () => {
    setShowProjectDialog(false)
  }

  const onApplicationCreate = () => {
    dispatch(setApplicationItem({ applicationItem: null }))
    setShowProjectDialog(true)
  }

  const onProjectSubmit = (project) => {
    if (isProject) {
      navigate(`/application-implementation/new?project=${project}`, { replace: true })
    } else {
      navigate(`/application/new?project=${project}`, { replace: true })
    }
  }

  return (
    <Layout>
      <HeaderNavigation activeKey="1" />
      <Content className="site-layout" style={{ padding: '85px 50px', minHeight: '100vh' }}>
        <PageHeader
          ghost={false}
          title="Заявки"
          extra={[
            <Button key="1" type="primary" onClick={onApplicationCreate}>
              Новая заявка
            </Button>,
          ]}
        />
        <div className="card-container">
          <Tabs onChange={onTabSelect} type="card" activeKey={applicationState}>
            <TabPane
              tab={
                <TabBadge
                  count={counts?.applicationItems?.count}
                  title="Активные заявки"
                  isActive={applicationState === APP_STATUS_ACTIVE}
                />
              }
              key="active"
            >
              <div>
                <ApplicationItemsTable isActive={applicationState === APP_STATUS_ACTIVE} />
              </div>
            </TabPane>
            <TabPane
              tab={
                <TabBadge
                  count={counts?.applications[APP_STATUS_PROGRESS]}
                  title="В работе"
                  isActive={applicationState === APP_STATUS_PROGRESS}
                />
              }
              key="progress"
            >
              <div>
                <ApplicationsTable
                  tableState={APP_STATUS_PROGRESS}
                  state={applicationState}
                  onStateChange={onStateChange}
                />
              </div>
            </TabPane>
            <TabPane
              tab={
                <TabBadge
                  count={counts?.applications[APP_STATUS_AGREE]}
                  title="На согласовании"
                  isActive={applicationState === APP_STATUS_AGREE}
                />
              }
              key="agree"
            >
              <div>
                <ApplicationsTable
                  tableState={APP_STATUS_AGREE}
                  state={applicationState}
                  onStateChange={onStateChange}
                />
              </div>
            </TabPane>
            <TabPane
              tab={
                <TabBadge
                  count={counts?.applications[APP_STATUS_PAY]}
                  title="Ожидает оплаты"
                  isActive={applicationState === APP_STATUS_PAY}
                />
              }
              key="pay"
            >
              <div>
                <ApplicationsTable tableState={APP_STATUS_PAY} state={applicationState} onStateChange={onStateChange} />
              </div>
            </TabPane>
            <TabPane
              tab={
                <TabBadge
                  count={counts?.applications[APP_STATUS_DELIVERY]}
                  title="Ожидает доставки"
                  isActive={applicationState === APP_STATUS_DELIVERY}
                />
              }
              key="delivery"
            >
              <div>
                <ApplicationsTable
                  tableState={APP_STATUS_DELIVERY}
                  state={applicationState}
                  onStateChange={onStateChange}
                />
              </div>
            </TabPane>
            <TabPane tab="Доставлены" key="done">
              <div>
                <ApplicationsTable
                  tableState={APP_STATUS_DONE}
                  state={applicationState}
                  onStateChange={onStateChange}
                />
              </div>
            </TabPane>
            <TabPane tab="Все заявки" key="all">
              <div>
                <ApplicationsTable tableState="all" state={applicationState} onStateChange={onStateChange} />
              </div>
            </TabPane>
          </Tabs>
        </div>
        <ChooseProjectModal
          showModal={showProjectDialog}
          onFinish={onProjectSubmit}
          onChancel={onProjectDialogCancel}
        />
      </Content>
    </Layout>
  )
}

export default Applications
